import { OrderedRow } from '../models/priceRows.model';
import { QuotationVersion } from '../models/quotationVersion.model';
import { PriceTable, bidPrices, bidRowValues, valueKey } from './bidTotal';
import { visibleRows } from './priceFieldRows';
import { RowValue } from './priceRowValues';

export type ExportedColumn = { supplierId: string; refVersion: number };

export type ExportedCell = { amount: number; unit?: string } | { text: string };

export type ExportedRow = { name: string; cells: Record<string, ExportedCell> };

const hasBid = (versions: QuotationVersion[], { supplierId, refVersion }: ExportedColumn) =>
  !!versions[refVersion]?.supplierBids?.some(bid => bid.supplierId === supplierId);

const cellOf = (
  row: OrderedRow,
  table: PriceTable,
  column: ExportedColumn,
  values: Map<string, RowValue>,
): ExportedCell | undefined => {
  const entered = table.enteredValues[valueKey(row.id, column.supplierId, column.refVersion)];
  if (row.kind === 'text') return entered?.text ? { text: entered.text } : undefined;
  if (row.kind === 'value') return entered?.value == null ? undefined : { amount: entered.value, unit: row.unit };

  const { amount, unit } = values.get(row.id) ?? {};
  return amount === undefined ? undefined : { amount, unit };
};

export const exportedPriceRows = (
  table: PriceTable,
  versions: QuotationVersion[],
  columns: ExportedColumn[],
  { sumName, fallbackUnit }: { sumName: string; fallbackUnit: string },
): ExportedRow[] => {
  const pricesOf = (column: ExportedColumn) =>
    bidPrices(versions, column.refVersion, column.supplierId, fallbackUnit);
  const pricedIds = new Set(columns.flatMap(pricesOf).map(price => price.id));
  const rows = visibleRows(table.rows, pricedIds).map(row => ({
    row,
    name: row.kind === 'sum' ? sumName : row.name,
    cells: {} as Record<string, ExportedCell>,
  }));

  for (const column of columns.filter(column => hasBid(versions, column))) {
    const values = bidRowValues(table, pricesOf(column), column.supplierId, column.refVersion);
    for (const { row, cells } of rows) {
      const cell = cellOf(row, table, column, values);
      if (cell) cells[column.supplierId] = cell;
    }
  }
  return rows.map(({ name, cells }) => ({ name, cells }));
};
